/** Kanata's Meta (Facebook) Pixel, from Kanata's marketing team. Not a secret: it ships in every page. */
export const META_PIXEL_ID = "2197628483708340";

/**
 * The hosts the pixel reports from: the live storefront only.
 *
 * dev.kanataindonesia.com, previews and localhost stay silent, so testing the
 * shop never shows up as traffic (or, later, as purchases) in Kanata's ads
 * account.
 */
const LIVE_HOSTS = new Set(["kanataindonesia.com", "www.kanataindonesia.com"]);

export function pixelEnabledOn(hostname: string): boolean {
  return LIVE_HOSTS.has(hostname.toLowerCase());
}

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[][];
  push: Fbq;
  loaded: boolean;
  version: string;
};

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

/**
 * Meta's base code, minus its own `track('PageView')`: the storefront is a
 * single-page app, so the page view is sent by `trackPageView` on every route
 * change instead, the first one included. Safe to call more than once.
 */
function loadPixel(): Fbq {
  if (window.fbq) return window.fbq;

  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue.push(args);
  } as Fbq;
  fbq.queue = [];
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  window.fbq = fbq;
  if (!window._fbq) window._fbq = fbq;

  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(script);

  fbq("init", META_PIXEL_ID);
  return fbq;
}

/** Report a page view, loading the pixel first if needed. Does nothing off the live storefront. */
export function trackPageView(): void {
  trackPixel("PageView");
}

/**
 * One standard event. `eventId` is Meta's deduplication key: the Purchase
 * passes the order number, which is what the server's Conversions API copy
 * sends too, so Meta keeps one of the two.
 */
export function trackPixel(event: string, params?: Record<string, unknown>, eventId?: string): void {
  if (typeof window === "undefined" || !pixelEnabledOn(window.location.hostname)) return;
  const fbq = loadPixel();
  if (eventId) fbq("track", event, params ?? {}, { eventID: eventId });
  else if (params) fbq("track", event, params);
  else fbq("track", event);
}

/**
 * The Pixel's first-party cookies, for the server's Conversions API copy of
 * the purchase. They're how Meta ties a server event to the browser that saw
 * the ad. Empty when the Pixel never ran (off the live host, or blocked).
 */
export function pixelCookies(cookie = typeof document === "undefined" ? "" : document.cookie): {
  fbp?: string;
  fbc?: string;
} {
  const get = (name: string) =>
    cookie
      .split(";")
      .map((c) => c.trim())
      .find((c) => c.startsWith(name + "="))
      ?.slice(name.length + 1) || undefined;
  return { fbp: get("_fbp"), fbc: get("_fbc") };
}
